package com.rosario.juridico.domain

import com.rosario.juridico.platform.FirebaseProxy
import com.rosario.juridico.platform.UserStatus
import com.rosario.juridico.getPersistedString
import com.rosario.juridico.persistString
import com.rosario.juridico.getPersistedLong
import com.rosario.juridico.persistLong
import com.rosario.juridico.premium.PremiumManager
import androidx.compose.runtime.snapshotFlow
import kotlinx.coroutines.CoroutineScope
import kotlinx.coroutines.Dispatchers
import kotlinx.coroutines.Job
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.StateFlow
import kotlinx.coroutines.flow.asStateFlow
import kotlinx.coroutines.flow.collectLatest
import kotlinx.coroutines.flow.distinctUntilChanged
import kotlinx.coroutines.flow.MutableSharedFlow
import kotlinx.coroutines.flow.asSharedFlow
import kotlinx.coroutines.SupervisorJob
import kotlinx.coroutines.withContext
import kotlinx.coroutines.launch
import kotlinx.coroutines.delay

enum class AccessState {
    PREMIUM_ACTIVE,
    EXPIRED
}

enum class VerificationStatus {
    VERIFIED,
    GRACE_PERIOD,
    EXPIRED_UNVERIFIED
}

class AppUsageManager {
    private val scope = CoroutineScope(Dispatchers.Default + SupervisorJob())
    
    private val _userStatus = MutableStateFlow(UserStatus())
    val userStatus: StateFlow<UserStatus> = _userStatus.asStateFlow()

    private val _usageNotifications = MutableSharedFlow<String>(extraBufferCapacity = 1)
    val usageNotifications = _usageNotifications.asSharedFlow()

    private val _verificationStatus = MutableStateFlow(VerificationStatus.VERIFIED)
    val verificationStatus: StateFlow<VerificationStatus> = _verificationStatus.asStateFlow()

    private val _currentUserEmail = MutableStateFlow<String?>(null)
    val currentUserEmail: StateFlow<String?> = _currentUserEmail.asStateFlow()

    private val _graceRemainingMillis = MutableStateFlow(0L)
    val graceRemainingMillis: StateFlow<Long> = _graceRemainingMillis.asStateFlow()

    private var observationJob: Job? = null
    private var verificationJob: Job? = null
    private var lastUsageCount: Int = -1
    private var lastMonthlyUsageCount: Int = -1
    
    // Flag de seguridad para evitar múltiples peticiones concurrentes
    private var isConsuming = false

    companion object {
        val instance = AppUsageManager()
        
        // Estado global para Seamless UX (Redirección post-login)
        var pendingRoute: String? = null
        var pendingAiQuery: String? = null
    }

    init {
        loadCachedStatus()
        observeAuth()
        observeStatus()
        observePremiumManager()
        observeVerificationStatus()
    }

    private fun observeAuth() {
        scope.launch {
            FirebaseProxy.observeAuthState().collect { uid ->
                _currentUserEmail.value = FirebaseProxy.getUserEmail()
                if (uid != null && !FirebaseProxy.isAnonymous()) {
                    startObserving(uid)
                    PremiumManager.loadUserProfile(uid)
                }
            }
        }
    }

    /**
     * Inicia la observación del estado del usuario en Firebase.
     * Se debe llamar después de que el usuario haya iniciado sesión (anónima o real).
     */
    fun startObserving(uid: String) {
        observeStatus()
        observeVerificationStatus()
    }

    private fun observeVerificationStatus() {
        verificationJob?.cancel()
        verificationJob = scope.launch {
            while (true) {
                val isVerified = FirebaseProxy.isEmailVerified()
                val isAnonymous = FirebaseProxy.isAnonymous()
                val isPremium = _userStatus.value.isPremium
                
                if (isVerified || (!isAnonymous && isPremium)) {
                    _verificationStatus.value = VerificationStatus.VERIFIED
                    _graceRemainingMillis.value = 0L
                } else {
                    _verificationStatus.value = VerificationStatus.GRACE_PERIOD
                    _graceRemainingMillis.value = -1L
                }
                delay(30000) // Re-validar cada 30 segundos
            }
        }
    }

    private fun observePremiumManager() {
        scope.launch {
            snapshotFlow { PremiumManager.isPremium to PremiumManager.entitlement }
                .distinctUntilChanged()
                .collect { (isPremium, entitlement) ->
                    _userStatus.value = _userStatus.value.copy(
                        isPremium = isPremium || PremiumManager.isPromoEntitlementActive(),
                        isInitialized = true,
                        premiumExpiresAt = entitlement.expiresAt,
                        consistency = entitlement.consistency
                    )
                }
        }
    }

    private fun loadCachedStatus() {
        val isPremium = getPersistedString("user_is_premium") == "true"
        val vencimiento = getPersistedLong("user_vencimiento")
        val trialExpiresAt = getPersistedLong("user_trial_expires_at")
        val initialUses = getPersistedLong("user_initial_uses").toInt().coerceAtLeast(0)
        val monthlyUses = getPersistedLong("user_monthly_uses").toInt().coerceAtLeast(0)

        if (getPersistedString("user_is_premium") != null) {
            _userStatus.value = UserStatus(
                isInitialized = true,
                isPremium = isPremium, 
                premiumExpiresAt = vencimiento,
                trialExpiresAt = trialExpiresAt,
                initial_uses_remaining = if (initialUses == 0 && getPersistedLong("user_initial_uses") == 0L) 5 else initialUses,
                monthly_uses_remaining = if (monthlyUses == 0 && getPersistedLong("user_monthly_uses") == 0L) 3 else monthlyUses,
                consistency = com.rosario.juridico.premium.EntitlementConsistencyState.LOCAL_ONLY
            )
        }
    }

    private fun observeStatus() {
        observationJob?.cancel()
        observationJob = scope.launch {
            FirebaseProxy.observeUserStatus().collectLatest { status ->
                println("AppDebug: AppUsageManager recibió nuevo status. Usos: ${status.initial_uses_remaining}, Mensuales: ${status.monthly_uses_remaining}")
                
                // Lógica de Alertas de Usos (UX Improvement)
                if (!status.isPremium && !PremiumManager.isPremium) {
                    // 1. Alertas para Usos Iniciales
                    if (lastUsageCount != -1 && status.initial_uses_remaining != lastUsageCount) {
                        val newCount = status.initial_uses_remaining
                        if (newCount == 2) {
                            _usageNotifications.emit("Te quedan 2 usos gratuitos, por menos de un café hazte premium y obten usos ilimitados")
                        } else if (newCount == 1) {
                            _usageNotifications.emit("¡Atención! Te queda solo 1 uso gratuito, por menos de un café hazte premium y obten usos ilimitados")
                        }
                    }

                    // 2. Alertas para Usos Mensuales (Solo si ya agotó los iniciales)
                    if (status.initial_uses_remaining == 0 && lastMonthlyUsageCount != -1 && status.monthly_uses_remaining != lastMonthlyUsageCount) {
                        val newCount = status.monthly_uses_remaining
                        if (newCount == 2) {
                            _usageNotifications.emit("Te quedan 2 usos gratuitos, por menos de un café hazte premium y obten usos ilimitados")
                        } else if (newCount == 1) {
                            _usageNotifications.emit("¡Atención! Te queda solo 1 uso gratuito, por menos de un café hazte premium y obten usos ilimitados")
                        }
                    }
                }
                
                lastUsageCount = status.initial_uses_remaining
                lastMonthlyUsageCount = status.monthly_uses_remaining

                val isAnon = FirebaseProxy.isAnonymous()
                var sanitizedStatus = if (isAnon) status.copy(isPremium = false) else status

                // 🛡️ GUARDIA DE SEGURIDAD EN APP USAGE MANAGER (Anulación de Lectura Falsa en Firestore):
                // Si PremiumManager confirma estatus Premium (o beneficio promocional activo),
                // forzamos isPremium = true anulando cualquier lectura obsoleta o 'false' proveniente de Firestore.
                val isGlobalPremium = !isAnon && (PremiumManager.isPremium || PremiumManager.isPromoEntitlementActive())
                if (isGlobalPremium || sanitizedStatus.isPremium) {
                    println("AppDebug: 🛡️ Escudo Activo en AppUsageManager: Forzando estatus Premium verdadero en _userStatus.")
                    sanitizedStatus = sanitizedStatus.copy(
                        isPremium = true,
                        isInitialized = true,
                        premiumExpiresAt = if (sanitizedStatus.premiumExpiresAt > 0) sanitizedStatus.premiumExpiresAt else PremiumManager.vencimiento
                    )
                }

                _userStatus.value = sanitizedStatus

                if (!isAnon) {
                    // Guardar en caché para carga instantánea en el próximo inicio
                    persistString("user_is_premium", sanitizedStatus.isPremium.toString())
                    persistLong("user_vencimiento", sanitizedStatus.premiumExpiresAt)
                    persistLong("user_trial_expires_at", sanitizedStatus.trialExpiresAt)
                    persistLong("user_initial_uses", sanitizedStatus.initial_uses_remaining.toLong())
                    persistLong("user_monthly_uses", sanitizedStatus.monthly_uses_remaining.toLong())
                } else {
                    persistString("user_is_premium", "false")
                    persistLong("user_vencimiento", 0L)
                }
            }
        }
    }

    /**
     * Motor Lógico de Consumo (Fase 2)
     * Ejecuta la validación y el descuento atómico de usos en Firestore.
     */
    suspend fun checkAndConsumeUsage(): Boolean = withContext(Dispatchers.Default) {
        val status = _userStatus.value
        // PRIORIDAD SEGURIDAD: Consultamos la fuente de verdad en tiempo real que sí evalúa el reloj del sistema.
        val isRealTimePremium = PremiumManager.isPremium

        println("AppDebug: Iniciando checkAndConsumeUsage. Status: isInitialized=${status.isInitialized}, isPremiumRT=$isRealTimePremium, uses=${status.initial_uses_remaining}")
        
        if (!status.isInitialized) {
            println("AppDebug: Consumo denegado - Estado no inicializado")
            return@withContext false
        }
        
        if (isRealTimePremium) {
            println("AppDebug: Consumo permitido - Acceso Ilimitado Premium (RT-Validated)")
            return@withContext true
        }

        if (isConsuming) {
            println("AppDebug: Consumo bloqueado - Ya hay una transacción en curso")
            return@withContext false
        }
        isConsuming = true

        try {
            val uid = FirebaseProxy.getCurrentUserId()
            if (uid == null) {
                println("AppDebug: Consumo denegado - UID nulo")
                false
            } else {
                println("AppDebug: Ejecutando transacción Firestore para UID: $uid")
                val result = FirebaseProxy.checkAndConsumeUsage(uid)
                println("AppDebug: Resultado de transacción: $result")
                val hasAccess = result != com.rosario.juridico.platform.AccessResult.DENIED
                if (hasAccess) {
                    com.rosario.juridico.platform.AnalyticsLogger.logEvent("free_use_consumed", mapOf("uid" to uid, "type" to result.name))
                }
                hasAccess
            }
        } catch (e: Exception) {
            println("AppDebug: ERROR CRÍTICO en checkAndConsumeUsage: ${e.message}")
            false
        } finally {
            isConsuming = false
        }
    }

    /**
     * Evalúa el acceso actual del usuario basado en tiempo.
     */
    fun checkAccess(): AccessState {
        // Cortocircuito para usuarios anónimos o sin email
        if (FirebaseProxy.isAnonymous() || _currentUserEmail.value.isNullOrBlank()) {
            return AccessState.EXPIRED
        }

        // PRIORIDAD ABSOLUTA: Si el Manager de Membresía confirma Premium, retornamos activo.
        if (com.rosario.juridico.premium.PremiumManager.isPremium) return AccessState.PREMIUM_ACTIVE

        val status = _userStatus.value
        val now = kotlinx.datetime.Clock.System.now().toEpochMilliseconds()

        return when {
            // Aplicamos lenidad: si el flag isPremium está activo, y el vencimiento es 0 (no definido) 
            // o futuro, consideramos que el acceso es válido.
            status.isPremium && (status.premiumExpiresAt <= 0L || status.premiumExpiresAt > now) -> AccessState.PREMIUM_ACTIVE
            else -> AccessState.EXPIRED
        }
    }

    /**
     * Helper de QA para simular estado Premium forzando la emisión de un nuevo objeto.
     */
    fun debugSimulatePremium() {
        _userStatus.value = _userStatus.value.copy(isPremium = true)
    }

    val isPremium: StateFlow<Boolean> by lazy {
        val flow = MutableStateFlow(_userStatus.value.isPremium)
        scope.launch {
            _userStatus.collect { status ->
                flow.value = status.isPremium
            }
        }
        flow.asStateFlow()
    }

    val isPremiumUser: StateFlow<Boolean> get() = isPremium
}
